import { createContext, useContext } from 'react';
import { PhoneAnalyticsTypes } from '@frontend/api-analytics';

// Structural subset of the phone analytics demo data that the routing charts read.
export interface RoutingAnalyticsDemoData {
  chartsData: {
    phoneTreesCallVolume: {
      data: unknown[];
      phoneNumbers: unknown;
      locationNames: Record<string, string>;
    };
  };
}

export interface RoutingAnalyticsData {
  filters: PhoneAnalyticsTypes.Filters;
  isDemoAccount: boolean | undefined;
  filterHintText: string;
  previousGlsLocationIds: string | undefined;
  demoData: RoutingAnalyticsDemoData | null;
  accessibleLocationsNames: Record<string, string>;
  // True when rendered outside the phone analytics page (e.g. communication analytics).
  isEmbedded: boolean;
}

const RoutingAnalyticsContext = createContext<RoutingAnalyticsData | null>(null);

export const RoutingAnalyticsProvider = RoutingAnalyticsContext.Provider;

/**
 * Returns `true` when the routing analytics panel is rendered outside the phone
 * analytics page (e.g. from the communication analytics page). Used to adjust
 * navigation behaviour.
 */
export const useIsRoutingAnalyticsEmbedded = (): boolean => {
  return useContext(RoutingAnalyticsContext)?.isEmbedded ?? false;
};

/**
 * Hook that returns the routing analytics data supplied by the enclosing
 * `RoutingAnalyticsProvider`. The provider is required — each consuming page
 * (phone or communication analytics) feeds the data from its own store.
 */
export const useRoutingAnalyticsData = (): RoutingAnalyticsData => {
  const contextValue = useContext(RoutingAnalyticsContext);
  if (!contextValue) {
    throw new Error('useRoutingAnalyticsData must be used within a RoutingAnalyticsProvider');
  }
  return contextValue;
};
